import type { Point, XY } from '@imagise/shared';
import { CropCalculator, getCropAspectRatio } from './crop-calculator';
import { cloneCropRect, getCropHandlePoints, normalizeCropRect } from './crop-rect';
import { DEFAULT_CROP_PRESENTATION, getCropCursor } from './crop-presentation';
import { CropOverlayRenderer } from './crop-overlay-renderer';
import type { EditorCanvas } from '../editor-canvas';
import type {
  CropControllerPointerEvent,
  CropOperation,
  CropOptions,
  CropPresentation,
  CropRect,
} from './types';
import { CropOperation as Operation } from './types';

type CropDragState = {
  readonly pointerId: number;
  readonly operation: CropOperation;
  lastX: number;
  lastY: number;
};

type ControllerOptions = {
  presentation?: CropPresentation;
};

export class EditorCanvasCropController {
  onRectChanged?: (rect: CropRect | null) => void;

  private readonly overlayCanvas: HTMLCanvasElement;
  private readonly overlayRenderer: CropOverlayRenderer;
  private readonly cropCalculator = new CropCalculator();
  private readonly detachAfterRender: () => void;
  private readonly detachResize: () => void;

  private cropOptions: CropOptions | null = null;
  private cropRect: CropRect | null = null;
  private cropDragState: CropDragState | null = null;
  private presentation: CropPresentation;

  private readonly onPointerDownBound = (event: Event) => {
    this.handlePointerDown(event as PointerEvent);
  };
  private readonly onPointerMoveBound = (event: Event) => {
    this.handlePointerMove(event as PointerEvent);
  };
  private readonly onPointerUpBound = (event: Event) => {
    this.handlePointerUp(event as PointerEvent);
  };
  private readonly onPointerLeaveBound = () => {
    if (!this.cropDragState) {
      this.overlayCanvas.style.cursor = 'default';
    }
  };

  constructor(
    private readonly canvas: EditorCanvas,
    options: ControllerOptions = {},
  ) {
    const runtimeDocument = this.canvas.wrapperEl?.ownerDocument ?? document;
    this.presentation = options.presentation ?? DEFAULT_CROP_PRESENTATION;
    this.overlayCanvas = runtimeDocument.createElement('canvas');
    this.overlayCanvas.style.position = 'absolute';
    this.overlayCanvas.style.left = '0';
    this.overlayCanvas.style.top = '0';
    this.overlayCanvas.style.width = '100%';
    this.overlayCanvas.style.height = '100%';
    this.overlayCanvas.style.zIndex = '2';
    this.overlayCanvas.style.background = 'transparent';
    this.overlayCanvas.style.touchAction = 'none';
    this.overlayCanvas.style.pointerEvents = 'none';
    this.overlayCanvas.setAttribute('draggable', 'false');
    this.canvas.wrapperEl?.append(this.overlayCanvas);

    this.overlayRenderer = new CropOverlayRenderer(this.overlayCanvas, this.presentation);
    this.syncOverlaySize();
    this.attachOverlayListeners();

    this.detachAfterRender = this.canvas.on('after:render', () => {
      this.syncOverlaySize();
      this.render();
    });
    this.detachResize = this.canvas.on('canvas:resized', () => {
      this.syncOverlaySize();
      this.render();
    });
  }

  get overlayElement(): HTMLCanvasElement {
    return this.overlayCanvas;
  }

  getRect(): CropRect | null {
    this.ensureCropRectInitialized();
    return this.cropRect ? { ...this.cropRect } : null;
  }

  getNormalizedRect(): CropRect | null {
    this.ensureCropRectInitialized();
    return normalizeCropRect(this.cropRect);
  }

  setOptions(options: CropOptions | null): void {
    this.cropOptions = options ? { ...options } : null;
    this.overlayCanvas.style.pointerEvents = this.cropOptions ? 'auto' : 'none';
    this.overlayCanvas.style.cursor = 'default';
    if (!this.cropOptions) {
      this.cropDragState = null;
      this.cropRect = null;
      this.overlayRenderer.clear();
      return;
    }

    this.ensureCropRectInitialized();
    this.render();
  }

  setRect(rect: CropRect | null): void {
    this.cropRect = rect ? cloneCropRect(rect) : null;
    this.render();
  }

  setPresentation(presentation: CropPresentation): void {
    this.presentation = presentation;
    this.overlayRenderer.setPresentation(presentation);
    this.render();
  }

  reset(): void {
    this.cropDragState = null;
    this.cropRect = null;
    this.cropOptions = null;
    this.overlayCanvas.style.pointerEvents = 'none';
    this.overlayCanvas.style.cursor = 'default';
    this.overlayRenderer.clear();
  }

  render(): void {
    this.syncOverlaySize();
    this.ensureCropRectInitialized();

    if (!this.cropOptions || !this.cropRect) {
      this.overlayRenderer.clear();
      return;
    }

    this.overlayRenderer.render({
      cropRect: this.cropRect,
      imageToScreen: point => this.imageToScreen(point),
    });
  }

  dispose(): void {
    this.detachAfterRender();
    this.detachResize();
    this.detachOverlayListeners();
    this.overlayCanvas.style.pointerEvents = 'none';
    this.overlayCanvas.style.cursor = 'default';
    this.overlayRenderer.clear();
    this.overlayCanvas.remove();
  }

  imageToScreen(point: Point | XY): Point | undefined {
    return this.canvas.imageToScreen(point);
  }

  screenToImage(point: Point | XY): Point | undefined {
    return this.canvas.screenToImage(point);
  }

  handlePointerDown(event: CropControllerPointerEvent): void {
    if (!this.cropOptions) {
      return;
    }

    this.ensureCropRectInitialized();
    if (!this.cropRect) {
      return;
    }

    const imagePoint = this.toImagePoint(event);
    if (!imagePoint) {
      return;
    }

    const operation = this.resolveCropOperation(imagePoint);
    if (operation === Operation.None) {
      return;
    }

    this.cropDragState = {
      pointerId: event.pointerId,
      operation,
      lastX: imagePoint.x,
      lastY: imagePoint.y,
    };

    if (typeof this.overlayCanvas.setPointerCapture === 'function') {
      this.overlayCanvas.setPointerCapture(event.pointerId);
    }

    event.preventDefault?.();
  }

  handlePointerMove(event: CropControllerPointerEvent): void {
    if (!this.cropOptions) {
      return;
    }

    this.ensureCropRectInitialized();
    if (!this.cropRect) {
      return;
    }

    const imagePoint = this.toImagePoint(event);
    if (!imagePoint) {
      return;
    }

    if (!this.cropDragState) {
      this.overlayCanvas.style.cursor = getCropCursor(this.resolveCropOperation(imagePoint));
      return;
    }

    if (this.cropDragState.pointerId !== event.pointerId) {
      return;
    }

    const imageSize = this.getAppliedImageSize();
    if (!imageSize) {
      return;
    }

    const dx = imagePoint.x - this.cropDragState.lastX;
    const dy = imagePoint.y - this.cropDragState.lastY;
    this.cropDragState.lastX = imagePoint.x;
    this.cropDragState.lastY = imagePoint.y;

    this.cropCalculator.transformCropRect(this.cropRect, {
      operation: this.cropDragState.operation,
      dx,
      dy,
      aspectRatio: getCropAspectRatio(this.cropOptions),
      centerAsBase: Boolean(event.ctrlKey),
      imageSize,
    });

    this.onRectChanged?.(this.getNormalizedRect());
    this.render();
    event.preventDefault?.();
  }

  handlePointerUp(event: CropControllerPointerEvent): void {
    if (!this.cropDragState || this.cropDragState.pointerId !== event.pointerId) {
      return;
    }

    this.cropRect = normalizeCropRect(this.cropRect);
    this.cropDragState = null;
    this.overlayCanvas.style.cursor = 'default';

    if (
      typeof this.overlayCanvas.hasPointerCapture === 'function' &&
      this.overlayCanvas.hasPointerCapture(event.pointerId) &&
      typeof this.overlayCanvas.releasePointerCapture === 'function'
    ) {
      this.overlayCanvas.releasePointerCapture(event.pointerId);
    }

    this.onRectChanged?.(this.getNormalizedRect());
    this.render();
    event.preventDefault?.();
  }

  private attachOverlayListeners(): void {
    this.overlayCanvas.addEventListener('pointerdown', this.onPointerDownBound);
    this.overlayCanvas.addEventListener('pointermove', this.onPointerMoveBound);
    this.overlayCanvas.addEventListener('pointerup', this.onPointerUpBound);
    this.overlayCanvas.addEventListener('pointercancel', this.onPointerUpBound);
    this.overlayCanvas.addEventListener('pointerleave', this.onPointerLeaveBound);
  }

  private detachOverlayListeners(): void {
    this.overlayCanvas.removeEventListener('pointerdown', this.onPointerDownBound);
    this.overlayCanvas.removeEventListener('pointermove', this.onPointerMoveBound);
    this.overlayCanvas.removeEventListener('pointerup', this.onPointerUpBound);
    this.overlayCanvas.removeEventListener('pointercancel', this.onPointerUpBound);
    this.overlayCanvas.removeEventListener('pointerleave', this.onPointerLeaveBound);
  }

  private syncOverlaySize(): void {
    this.overlayRenderer.resize(this.canvas.width, this.canvas.height);
  }

  private ensureCropRectInitialized(): void {
    if (!this.cropOptions || this.cropRect) {
      return;
    }

    const imageSize = this.getAppliedImageSize();
    if (!imageSize) {
      return;
    }

    this.cropRect = this.cropCalculator.getInitialCropRect(this.cropOptions, imageSize);
    this.onRectChanged?.(this.getNormalizedRect());
  }

  private getAppliedImageSize(): { width: number; height: number } | undefined {
    return this.canvas.getRasterLayer()?.getCurrentSize(false);
  }

  private toImagePoint(event: Pick<CropControllerPointerEvent, 'clientX' | 'clientY'>): Point | undefined {
    const rect = this.overlayCanvas.getBoundingClientRect();

    return this.screenToImage({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  }

  private resolveCropOperation(point: XY): CropOperation {
    const normalizedRect = normalizeCropRect(this.cropRect);
    if (!normalizedRect) {
      return Operation.None;
    }

    const scale = this.getDisplayScale();
    const handleRadius = this.presentation.handleSize / (scale * 2);
    const handlePoints = getCropHandlePoints(normalizedRect);
    const operations: CropOperation[] = [
      Operation.ResizeN,
      Operation.ResizeNE,
      Operation.ResizeE,
      Operation.ResizeSE,
      Operation.ResizeS,
      Operation.ResizeSW,
      Operation.ResizeW,
      Operation.ResizeNW,
    ];

    for (let index = 0; index < handlePoints.length; index += 1) {
      const handle = handlePoints[index];
      const dx = point.x - handle.x;
      const dy = point.y - handle.y;
      if (dx * dx + dy * dy <= handleRadius * handleRadius) {
        return operations[index];
      }
    }

    const insideRect =
      point.x >= normalizedRect.x &&
      point.y >= normalizedRect.y &&
      point.x <= normalizedRect.x + normalizedRect.width &&
      point.y <= normalizedRect.y + normalizedRect.height;

    return insideRect ? Operation.Move : Operation.None;
  }

  private getDisplayScale(): number {
    const origin = this.imageToScreen({ x: 0, y: 0 });
    const nextX = this.imageToScreen({ x: 1, y: 0 });
    const nextY = this.imageToScreen({ x: 0, y: 1 });

    if (!origin || !nextX || !nextY) {
      return 1;
    }

    const scaleX = Math.hypot(nextX.x - origin.x, nextX.y - origin.y);
    const scaleY = Math.hypot(nextY.x - origin.x, nextY.y - origin.y);
    const scale = Math.max(scaleX, scaleY);
    return scale > 0 ? scale : 1;
  }
}
